import { useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterNoMatches } from '@components/ui/FilterNoMatches/FilterNoMatches';
import { LabelSqueezeContext } from '../../SchedulerPage/context/LabelSqueezeContext';
import { ActivityLadderContext, type ActivityByTask } from '../../SchedulerPage/hooks/useActivityLadder';
import { computeLabelPanel, LABEL_PANEL_MIN } from '../../SchedulerPage/lib/labelPanel';
import { IssueLane } from './IssueLane';
import { WorkPlanGroupRow } from './WorkPlanGroupRow';
import { WorkPlanHeader } from './WorkPlanHeader';
import { WorkPlanRow } from './WorkPlanRow';
import { isCompact, type IIssueLane, type IPlacedRow, type IPlanWindow, type IPromiseGroup, type PlanGroupBy } from './workPlanModel';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './workPlanGrid.module.css';

/** The rail at the Scheduler's fully squeezed geometry (`lib/labelPanel`):
 *  PROGRESS at its floor, STATUS a coloured circle under a blank title — the
 *  days get the width; the bar's colour already says the rung. */
const RAIL = computeLabelPanel(LABEL_PANEL_MIN);
const SQUEEZE = { asStatusCircle: RAIL.asStatusCircle };

export interface IWorkPlanGridProps {
  window: IPlanWindow;
  /** Null while every rung is unchecked — the lane alone. */
  groups: IPromiseGroup<IPlacedRow>[] | null;
  /** Null when no issue is left to show. */
  lane: IIssueLane | null;
  groupBy: PlanGroupBy;
  today: string;
  /** The task whose drawer is open — its row reads as selected. */
  selectedTaskId: string | undefined;
  onOpenTask: (taskId: string) => void;
  onOpenIssue: (key: string) => void;
  /** Any of the page's filters is on — an empty grid is then their doing. */
  filtersActive: boolean;
  onClearFilters: () => void;
}

/**
 * The day grid, drawn as the Scheduler's Gantt draws itself: its rail columns
 * (task · profession · progress · status — the rail's own cells), its month /
 * week / day header, rest-day columns and today column. Then the pinned
 * issue lane and the promise rows under collapsible group bands — or the
 * empty note when the window holds none — each only while it has something
 * to hold. When the filters leave nothing at all, the shared "no items
 * match" state says so, with Clear, instead of a bare header.
 */
export function WorkPlanGrid({ window, groups, lane, groupBy, today, selectedTaskId, onOpenTask, onOpenIssue, filtersActive, onClearFilters }: IWorkPlanGridProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  // The rail's status cells read each task's rung the way the Gantt does —
  // from Trusstor's word for its activity, served here from the rows.
  const ladder = useMemo<ActivityByTask>(
    () =>
      new Map(
        (groups ?? []).flatMap((group) =>
          group.rows.map((row) => [row.task.id, { activityStatus: row.activityStatus, areas: {} }] as const),
        ),
      ),
    [groups],
  );
  const style = {
    '--wp-days': window.days.length,
    '--gantt-col-ppc': `${RAIL.ppcPx}px`,
    '--gantt-col-status': `${RAIL.statusPx}px`,
  } as CSSProperties;
  // Grouped by profession, the bands name the trade: the column goes.
  const withProfession = groupBy !== 'profession';
  const empty = groups?.every((group) => group.rows.length === 0);
  const noMatches = filtersActive && !lane && (groups === null || empty);
  return (
    <LabelSqueezeContext.Provider value={SQUEEZE}>
      <div className={styles.frame}>
        <ScrollArea
          orientation="both"
          className={styles.gridFrame}
          viewportClassName={styles.grid}
          viewportProps={{ style, 'data-without-profession': withProfession ? undefined : 'true' }}
        >
          <WorkPlanHeader window={window} compact={isCompact(window)} withProfession={withProfession} />
          {lane && <IssueLane lane={lane} window={window} today={today} onOpenIssue={onOpenIssue} />}
          {noMatches && <FilterNoMatches onClear={onClearFilters} className={styles.noMatches} />}
          {empty && !noMatches && <p className={styles.empty}>{t('activities.workPlan.empty')}</p>}
          {groups && !empty && (
            <ActivityLadderContext.Provider value={ladder}>
              {groups.map((group) => (
                <WorkPlanGroupRow
                  key={group.key}
                  group={group}
                  withProfession={withProfession}
                  collapsed={collapsed.has(group.key)}
                  onToggle={() => toggle(group.key)}
                >
                  {group.rows.map((row) => (
                    <WorkPlanRow
                      key={row.key}
                      row={row}
                      window={window}
                      today={today}
                      showProfession={withProfession}
                      showAssignee={groupBy !== 'assignee'}
                      selected={row.task.id === selectedTaskId}
                      onOpen={onOpenTask}
                    />
                  ))}
                </WorkPlanGroupRow>
              ))}
            </ActivityLadderContext.Provider>
          )}
        </ScrollArea>
      </div>
    </LabelSqueezeContext.Provider>
  );
}
